// Passenger Flow Animation Service - Color Wave Animation
// Manages color wave animations along passenger paths

interface PathCoordinate {
  x: number;
  y: number;
}

interface FlowAnimationData {
  assetCode: string;
  flowOut: number;
  pathCoordinates: PathCoordinate[];
  minFlow: number;
  maxFlow: number;
  dotPositions: number[]; // Fixed positions of dots along the path (0 to 1)
  wavePosition: number; // Current position of the color wave (0 to 1)
  speed: number; // Speed of wave movement
  lastUpdateTime: number;
}

class PassengerFlowAnimationService {
  private animationData: Map<string, FlowAnimationData> = new Map();
  private animationTimer: NodeJS.Timeout | null = null;
  private subscribers: Array<(data: Map<string, FlowAnimationData>) => void> = [];

  /**
   * Initialize flow animation for an asset with fixed dots and color wave
   */
  initializeFlow(
    assetCode: string, 
    flowOut: number, 
    pathCoordinates: PathCoordinate[],
    minFlow: number = 5,
    maxFlow: number = 20
  ): void {
    if (!pathCoordinates || pathCoordinates.length === 0) {
      return;
    }

    // Calculate speed based on flow value
    const avgLowerBound = 1.3 * minFlow;
    const avgUpperBound = 0.75 * maxFlow;
    
    let speed: number;
    if (flowOut < avgLowerBound) {
      speed = 0.1; // MIN SPEED - slow wave for traffic jam
    } else if (flowOut > avgUpperBound) {
      speed = 0.5; // MAX SPEED - fast wave for smooth flow
    } else {
      speed = 0.25; // AVG SPEED - normal wave speed
    }

    // Calculate total path length
    let totalPathLength = 0;
    for (let i = 1; i < pathCoordinates.length; i++) {
      const dx = pathCoordinates[i].x - pathCoordinates[i - 1].x;
      const dy = pathCoordinates[i].y - pathCoordinates[i - 1].y;
      totalPathLength += Math.sqrt(dx * dx + dy * dy);
    }

    // Calculate dot spacing (increased by 3x from 0.3 to 0.9)
    const dotSpacing = 0.9; // Increased spacing between dots
    const numDots = Math.max(5, Math.floor(totalPathLength / dotSpacing));
    
    // Initialize fixed dot positions evenly along the path
    const dotPositions: number[] = [];
    for (let i = 0; i < numDots; i++) {
      // Distribute dots evenly from 0 to 1
      dotPositions.push(i / (numDots - 1));
    }

    this.animationData.set(assetCode, {
      assetCode,
      flowOut,
      pathCoordinates,
      minFlow,
      maxFlow,
      dotPositions,
      wavePosition: 0, // Start wave at beginning
      speed,
      lastUpdateTime: Date.now()
    });
  }

  /**
   * Update color wave position
   */
  updateFlows(): void {
    const now = Date.now();
    
    Array.from(this.animationData.entries()).forEach(([assetCode, data]) => {
      const elapsed = (now - data.lastUpdateTime) / 1000; // Convert to seconds
      
      // Update wave position based on speed
      let newWavePosition = data.wavePosition + (data.speed * elapsed);
      
      // Wrap around when wave reaches the end
      if (newWavePosition > 1.5) { // 1.5 to allow wave to fully exit before restarting
        newWavePosition = -0.5; // Start before the path so wave enters smoothly
      }
      
      data.wavePosition = newWavePosition;
      data.lastUpdateTime = now;
    });

    this.notifySubscribers();
  }

  /**
   * Calculate color intensity for a dot based on wave position
   * Returns a value between 0 and 1 (0 = base color, 1 = wave color)
   * Now affects TWO dots at a time with darker shading
   */
  getDotIntensity(dotPosition: number, wavePosition: number): number {
    // Wave now affects 2 dots (wider wave)
    const waveWidth = 0.4; // Increased from 0.3 to cover 2 dots
    const distance = Math.abs(dotPosition - wavePosition);
    
    if (distance < waveWidth) {
      // Smooth gradient within the wave
      // Using a sharper gradient for more pronounced effect
      return Math.pow(Math.cos((distance / waveWidth) * Math.PI / 2), 2);
    }
    
    return 0; // Outside wave
  }

  /**
   * Calculate total path length
   */
  private calculatePathLength(coordinates: PathCoordinate[]): number {
    let length = 0;
    for (let i = 1; i < coordinates.length; i++) {
      const dx = coordinates[i].x - coordinates[i - 1].x;
      const dy = coordinates[i].y - coordinates[i - 1].y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
    return length;
  }

  /**
   * Get position along path at a given fraction (0 to 1)
   */
  getPositionAlongPath(pathCoordinates: PathCoordinate[], fraction: number): PathCoordinate {
    if (fraction <= 0) return pathCoordinates[0];
    if (fraction >= 1) return pathCoordinates[pathCoordinates.length - 1];

    // Calculate total path length and segment lengths
    const segments: number[] = [];
    let totalLength = 0;
    
    for (let i = 1; i < pathCoordinates.length; i++) {
      const dx = pathCoordinates[i].x - pathCoordinates[i - 1].x;
      const dy = pathCoordinates[i].y - pathCoordinates[i - 1].y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);
      segments.push(segmentLength);
      totalLength += segmentLength;
    }

    // Find which segment contains the target position
    const targetLength = fraction * totalLength;
    let accumulatedLength = 0;
    
    for (let i = 0; i < segments.length; i++) {
      if (accumulatedLength + segments[i] >= targetLength) {
        // Interpolate within this segment
        const segmentFraction = (targetLength - accumulatedLength) / segments[i];
        const start = pathCoordinates[i];
        const end = pathCoordinates[i + 1];
        
        return {
          x: start.x + (end.x - start.x) * segmentFraction,
          y: start.y + (end.y - start.y) * segmentFraction
        };
      }
      accumulatedLength += segments[i];
    }

    return pathCoordinates[pathCoordinates.length - 1];
  }

  /**
   * Start animation loop
   */
  startAnimation(updateInterval: number = 50): void {
    if (this.animationTimer) {
      this.stopAnimation();
    }

    this.animationTimer = setInterval(() => {
      this.updateFlows();
    }, updateInterval);
  }

  /**
   * Stop animation loop
   */
  stopAnimation(): void {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
  }

  /**
   * Subscribe to animation updates
   */
  subscribe(callback: (data: Map<string, FlowAnimationData>) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all subscribers
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => callback(this.animationData));
  }

  /**
   * Get current animation data
   */
  getAnimationData(): Map<string, FlowAnimationData> {
    return this.animationData;
  }

  /**
   * Clear all animation data
   */
  clearAll(): void {
    this.animationData.clear();
    this.notifySubscribers();
  }
}

export const passengerFlowAnimationService = new PassengerFlowAnimationService();
export type { FlowAnimationData, PathCoordinate };